import { useHubble } from '@seek/hubble';
import { metrics } from '@seek/metrics-js';
import { useEffect } from 'react';
import { v4 as uuid } from 'uuid';

import type { VisibilityLevel } from 'src/__generated__/graphql';
import {
  type Page,
  type PageSection,
  useTrackingClient,
} from 'src/context/tracking';
import { useJobId } from 'src/context/useJobId';
import { tagRecording } from 'src/modules/hotjar';
import { useSetHubblePage } from 'src/modules/hubble';
import { getJobSolMetadata } from 'src/modules/solTracker/solMetadata';

const trackingCorrelationId = uuid();

export function useTracking({
  isCandidateNewToWorkforce,
  currentPage,
  pageSection,
}: {
  isCandidateNewToWorkforce: boolean;
  currentPage: Page;
  pageSection: PageSection;
}) {
  const jobId = useJobId();
  const hubble = useHubble();
  useSetHubblePage(currentPage);
  const client = useTrackingClient();
  const solMetadata = getJobSolMetadata(jobId);

  const sharedProps = {
    currentPage,
    pageTitle: currentPage,
    isExitLink: false,
    isCandidateNewToWorkforce,
    trackingCorrelationId,
  };

  useEffect(() => {
    client.trackView({
      eventName: 'apply_review_displayed',
      view: currentPage,
      currentPageSection: pageSection,
    });

    metrics.count('APPLY_REVIEW_DISPLAYED');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const trackSubmitSucceeded = ({
    profileVisibilityLevel,
    applicationId,
    applicationCorrelationId,
    isResumeAttached,
    isCoverLetterAttached,
    resumeHasSensitivePersonalInfo,
  }: {
    profileVisibilityLevel: VisibilityLevel;
    applicationId: string;
    applicationCorrelationId: string;
    isResumeAttached: boolean;
    isCoverLetterAttached: boolean;
    resumeHasSensitivePersonalInfo: boolean;
  }) => {
    hubble.trackEvent(
      'job_apply_submit_succeeded',
      {
        jobId,
        thirdPartyLink: false,
        solMetadata,
        applicationId,
        applicationCorrelationId,
      },
      { trackingCorrelationId },
    );

    client.trackLink({
      ...sharedProps,
      eventName: 'apply_submit_succeeded',
      link: 'apply_submit_succeeded',
      profileVisibilityLevel,
      isResumeAttached,
      isCoverLetterAttached,
      resumeHasSensitivePersonalInfo,
    });

    metrics.count('SUBMIT_APPLICATION_SUCCESS');
    tagRecording('application-submit-success');
  };

  const trackSubmitClick = ({
    profileVisibilityLevel,
  }: {
    profileVisibilityLevel: VisibilityLevel;
  }) => {
    hubble.trackEvent('job_apply_submit_pressed', { jobId, solMetadata });

    client.trackLink({
      ...sharedProps,
      eventName: 'application_submit_pressed',
      link: 'application_submit_pressed',
      profileVisibilityLevel,
    });
  };

  return { trackSubmitSucceeded, trackSubmitClick };
}
